import _get from 'lodash/get';
import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import NewBookingDuck from 'ducks/new-booking-duck';
import { newBookingOverlayType } from 'constants/new-booking-constants';
import ScribeDuck from 'ducks/scribe-duck';
import { Col, Icon, Row, Tooltip } from 'grab-ui';
import NumberedPin from 'components/v2/common/numbered-pin';
import I18n from 'i18n-js';
import DivButton from 'components/v2/div-button/div-button';
import InputField from 'components/v2/input-field/input-field';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { SCRIBE_STATE_NAMES } from 'applications/scribe/instrumentations/track';
import { NewBookingDeliveryStepPropType } from 'common/prop-types';
import SavedPoiCTAButton from 'components/v2/saved-poi-cta-button';
import usePostSavingPoiCallback from 'components/v2/common/hooks/use-post-saving-poi-callback';
import { onboardingCounterKey } from 'components/v2/contextual-onboarding';
import { deliveryFormFields, makeStepDetailFieldName } from './common';
import styles from './styles.less';

const savedPOITooltipOnboardingID = 'savedPOITooltip';

const DeliveryStep = ({
  form,
  dispatch,
  deliveryID,
  isPickUp,
  showCODField,
  deliveryStep,
  showNumber = false,
  index = 0,
  codDisabled = false,
  codDisabledHint = undefined,
  codCurrencySymbol = undefined,
  codLowerLimit = 0,
  codUpperLimit = 0,
  compulsoryCOD = false,
  canDelete,
  disabled,
  isRoundTrip,
  deliveryIndex,
}) => {
  const savedPOIOnboarding = useMemo(() => {
    if (!(deliveryIndex === 0 && index === 0)) {
      return false;
    }
    const key = onboardingCounterKey(savedPOITooltipOnboardingID);
    const value = window.localStorage.getItem(key);
    window.localStorage.setItem(key, '1');
    return !value;
  }, []);

  const poi = _get(deliveryStep, 'poi');

  const nameField = makeStepDetailFieldName(
    deliveryFormFields.name.fieldName,
    isPickUp,
    index,
  );
  const phoneField = makeStepDetailFieldName(
    deliveryFormFields.phone.fieldName,
    isPickUp,
    index,
  );

  const locationNoteField = makeStepDetailFieldName(
    deliveryFormFields.locationNote.fieldName,
    isPickUp,
    index,
  );

  const onEdit = useCallback(
    () => {
      dispatch(
        NewBookingDuck.creators.openDetailOverlay(
          deliveryID,
          isPickUp
            ? newBookingOverlayType.pickUpAddress
            : newBookingOverlayType.dropOffAddress,
        ),
      );

      if (isPickUp) {
        dispatch(
          ScribeDuck.creators.scribeExpressDeliveryDetailPickUpAddress({
            index,
            deliveryID,
          }),
        );
      } else {
        dispatch(
          ScribeDuck.creators.scribeExpressDeliveryDetailDropOffAddress({
            index,
            deliveryID,
          }),
        );
      }
    },
    [dispatch, deliveryID, isPickUp],
  );

  const onDelete = useCallback(
    removeIndex => () => {
      dispatch(NewBookingDuck.creators.removeDroppOff(deliveryID, removeIndex));
    },
    [dispatch, deliveryID],
  );

  const initialValue = _get(deliveryStep, deliveryFormFields.cod.fieldName);
  const codFieldSuffix = useMemo(
    () => {
      if (codDisabled && codDisabledHint) {
        return (
          <Tooltip title={codDisabledHint} placement={'top'}>
            <Icon type="info-circle" theme="outlined" color={'#708598'} />
          </Tooltip>
        );
      }
      return <span />;
    },
    [codDisabledHint, codDisabled],
  );

  // clear COD when it becomes disabled
  const codFieldID = makeStepDetailFieldName(
    deliveryFormFields.cod.fieldName,
    isPickUp,
    index,
  );
  useEffect(
    () => {
      if (codDisabled && form.getFieldValue(codFieldID)) {
        form.setFieldsValue({ [codFieldID]: '' });
      }
    },
    [codDisabled],
  );

  const postSavingPoiCallback = usePostSavingPoiCallback();

  return (
    <div className={styles.deliveryStep}>
      <div className={styles.iconWithDotLine}>
        {isRoundTrip && isPickUp ? (
          <NumberedPin
            className={styles.icon}
            showNumber={false}
            isSender={false}
            isRoundTrip
          />
        ) : (
          <NumberedPin
            className={styles.icon}
            showNumber={!isPickUp && showNumber}
            isSender={isPickUp}
            number={`${index + 1}`}
          />
        )}
        <div className={styles.dotLine} />
      </div>
      <div
        className={cx(styles.deliveryStepDetails, {
          [styles.savedPOIOnboarding]: savedPOIOnboarding,
        })}
      >
        {!poi ? (
          <Row type="flex" gutter={16}>
            <Col xs={24} md={24}>
              <div className={styles.emptyDeliveryStep} onClick={onEdit}>
                {isPickUp
                  ? I18n.t('v2.homepage.manualOrder.placeholder.senderLocation')
                  : I18n.t(
                      'v2.homepage.manualOrder.placeholder.recipientLocation',
                    )}
              </div>
            </Col>
          </Row>
        ) : (
          <Fragment>
            <Row type="flex" gutter={16}>
              <Col xs={24} md={24}>
                <div className={styles.poiName}>
                  <div
                    onClick={disabled ? undefined : onEdit}
                    className={disabled ? undefined : styles.clickable}
                  >
                    {_get(poi, 'address.name')}
                  </div>

                  <div className={styles.actionContainer}>
                    {!isPickUp &&
                      !disabled &&
                      canDelete && (
                        <DivButton
                          className={styles.closeIcon}
                          onClick={onDelete(index)}
                        >
                          <Icon type="grabicon-close-01" />
                        </DivButton>
                      )}
                    {savedPOIOnboarding ? (
                      <Tooltip
                        title={I18n.t(
                          'phrase.savedPoi.express_web_savedpoi_tooltip_save_favourite_dropoff',
                        )}
                        placement={'topRight'}
                        visible
                        overlayClassName={styles.savedPoiOnboardingTooltip}
                      >
                        <SavedPoiCTAButton
                          poi={poi}
                          isPickUp={isPickUp}
                          postSavingPoiCallback={postSavingPoiCallback(
                            deliveryID,
                            index,
                          )}
                          className={styles.savedPoiIcon}
                          scribeState={
                            SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB
                          }
                        />
                      </Tooltip>
                    ) : (
                      <SavedPoiCTAButton
                        poi={poi}
                        isPickUp={isPickUp}
                        postSavingPoiCallback={postSavingPoiCallback(
                          deliveryID,
                          index,
                        )}
                        className={styles.savedPoiIcon}
                        scribeState={
                          SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB
                        }
                      />
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            <Row type="flex" gutter={16}>
              <Col xs={24} md={16}>
                <InputField
                  form={form}
                  id={locationNoteField}
                  disabled={disabled}
                  fieldOptions={{
                    initialValue: _get(
                      deliveryStep,
                      deliveryFormFields.locationNote.fieldName,
                    ),
                    rules: deliveryFormFields.locationNote.makeRules({
                      maxLength: deliveryFormFields.locationNote.maxLength,
                    }),
                  }}
                  placeholder={I18n.t(
                    'v2.homepage.manualOrder.placeholder.locationNote',
                  )}
                  className={styles.locationNote}
                  fieldClassName={styles.locationNoteField}
                />
              </Col>
            </Row>

            <Row type="flex" gutter={16}>
              <Col xs={24} md={8}>
                <InputField
                  form={form}
                  id={nameField}
                  disabled={disabled}
                  fieldOptions={{
                    initialValue: _get(
                      deliveryStep,
                      deliveryFormFields.name.fieldName,
                      '',
                    ),
                    rules: deliveryFormFields.name.makeRules({ isPickUp }),
                  }}
                  placeholder={I18n.t(
                    isPickUp
                      ? 'booking.fieldPlaceholder.senderName'
                      : 'v2.homepage.manualOrder.placeholder.recipientName',
                  )}
                  fieldClassName={cx(styles.inputField, {
                    [styles.disabled]: disabled,
                  })}
                />
              </Col>
              <Col xs={24} md={8}>
                <InputField
                  form={form}
                  id={phoneField}
                  disabled={disabled}
                  fieldOptions={{
                    initialValue: _get(
                      deliveryStep,
                      deliveryFormFields.phone.fieldName,
                      '',
                    ),
                    rules: deliveryFormFields.phone.makeRules({ isPickUp }),
                  }}
                  placeholder={I18n.t(
                    'v2.homepage.manualOrder.placeholder.phone',
                  )}
                  fieldClassName={cx(styles.inputField, {
                    [styles.disabled]: disabled,
                  })}
                />
              </Col>
              {showCODField && (
                <Col xs={24} md={8}>
                  <InputField
                    form={form}
                    type="number"
                    disabled={codDisabled || disabled}
                    prefix={initialValue && codCurrencySymbol}
                    suffix={codFieldSuffix}
                    id={codFieldID}
                    fieldOptions={{
                      validateTrigger: 'onBlur',
                      rules: deliveryFormFields.cod.makeRules({
                        lowerLimit: codLowerLimit,
                        upperLimit: codUpperLimit,
                        compulsoryCOD,
                        disabled: codDisabled || disabled,
                      }),
                      initialValue,
                    }}
                    placeholder={
                      codDisabled
                        ? I18n.t(
                            'v2.anytimeBooking.deliveryForm.disabledCODPlaceholder',
                          )
                        : I18n.t('v2.homepage.manualOrder.placeholder.cod')
                    }
                    fieldClassName={styles.inputField}
                  />
                </Col>
              )}
            </Row>
          </Fragment>
        )}
      </div>
    </div>
  );
};

DeliveryStep.defaultProps = {
  index: 0,
  showNumber: false,
  isPickUp: false,
  codDisabled: false,
  codDisabledHint: '',
  codCurrencySymbol: '',
  codLowerLimit: 0,
  codUpperLimit: 0,
  compulsoryCOD: false,
  canDelete: false,
  disabled: false,
  isRoundTrip: false,
};

DeliveryStep.propTypes = {
  deliveryID: PropTypes.string.isRequired,
  form: PropTypes.shape({}).isRequired,
  dispatch: PropTypes.func.isRequired,
  deliveryStep: NewBookingDeliveryStepPropType.isRequired,
  isPickUp: PropTypes.bool,
  showCODField: PropTypes.bool.isRequired,
  showNumber: PropTypes.bool,
  index: PropTypes.number,
  codDisabled: PropTypes.bool,
  codDisabledHint: PropTypes.string,
  codCurrencySymbol: PropTypes.string,
  codLowerLimit: PropTypes.number,
  codUpperLimit: PropTypes.number,
  compulsoryCOD: PropTypes.bool,
  canDelete: PropTypes.bool,
  disabled: PropTypes.bool,
  isRoundTrip: PropTypes.bool,
  deliveryIndex: PropTypes.number.isRequired,
};

export default DeliveryStep;
